// Advanced SEO Performance Optimization JavaScript
// This script implements cutting-edge SEO techniques for maximum search visibility

class SEOOptimizer {
  constructor() {
    this.init();
  }

  init() {
    this.setupCriticalResourceHints();
    this.implementLazyLoading();
    this.optimizeImages();
    this.setupIntersectionObserver();
    this.implementStructuredDataTracking();
    this.setupPerformanceMonitoring();
    this.initializeSearchTracking();
  }

  // Critical Resource Hints for Performance
  setupCriticalResourceHints() {
    const head = document.head;
    
    // Preload critical fonts
    const fontPreloads = [
      '/assets/font-primary.woff2',
      '/assets/font-secondary.woff2'
    ];
    
    fontPreloads.forEach(font => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = font;
      link.as = 'font';
      link.type = 'font/woff2';
      link.crossOrigin = 'anonymous';
      head.appendChild(link);
    });

    // Prefetch next likely pages
    this.prefetchLikelyPages();
  }

  // Intelligent Page Prefetching
  prefetchLikelyPages() {
    const prefetchPages = [
      '/collections/all',
      '/pages/about',
      '/pages/contact'
    ];

    // Prefetch on user interaction hints
    let prefetchTimer;
    document.addEventListener('mouseover', (e) => {
      if (e.target.tagName === 'A') {
        clearTimeout(prefetchTimer);
        prefetchTimer = setTimeout(() => {
          this.prefetchPage(e.target.href);
        }, 100);
      }
    });

    // Prefetch popular pages after initial load
    setTimeout(() => {
      prefetchPages.forEach(page => this.prefetchPage(page));
    }, 2000);
  }

  prefetchPage(url) {
    if (!url || url.includes('mailto:') || url.includes('tel:')) return;
    
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  }

  // Advanced Lazy Loading Implementation
  implementLazyLoading() {
    const lazyImages = document.querySelectorAll('img[data-src]');
    const lazyVideos = document.querySelectorAll('video[data-src]');
    
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
          img.classList.add('loaded');
          observer.unobserve(img);
        }
      });
    }, {
      rootMargin: '50px 0px',
      threshold: 0.1
    });

    lazyImages.forEach(img => imageObserver.observe(img));
    lazyVideos.forEach(video => imageObserver.observe(video));
  }

  // Image Optimization
  optimizeImages() {
    // Add loading="lazy" to images below the fold
    const images = document.querySelectorAll('img');
    images.forEach((img, index) => {
      if (index > 3) { // Skip first 3 images (above the fold)
        img.loading = 'lazy';
      }
      
      // Add proper alt text if missing
      if (!img.alt) {
        const productTitle = img.closest('[data-product-title]')?.dataset.productTitle;
        if (productTitle) {
          img.alt = `${productTitle} - Product Image`;
        }
      }
    });
  }

  // Intersection Observer for SEO Events
  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          
          // Track product views
          if (element.dataset.productId) {
            this.trackProductView(element.dataset.productId);
          }
          
          // Track collection views
          if (element.dataset.collectionId) {
            this.trackCollectionView(element.dataset.collectionId);
          }
        }
      });
    }, { threshold: 0.5 });

    // Observe product cards
    document.querySelectorAll('[data-product-id]').forEach(el => {
      observer.observe(el);
    });
  }

  // Structured Data Tracking
  implementStructuredDataTracking() {
    // Track interactions for enhanced structured data
    document.addEventListener('click', (e) => {
      const target = e.target.closest('[data-product-id]');
      if (target) {
        this.trackProductInteraction(target.dataset.productId, 'click');
      }
    });

    // Track search interactions
    const searchForm = document.querySelector('form[action="/search"]');
    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        const query = e.target.querySelector('input[name="q"]')?.value;
        if (query) {
          this.trackSearchQuery(query);
        }
      });
    }
  }

  // Performance Monitoring
  setupPerformanceMonitoring() {
    // Core Web Vitals tracking
    this.trackCoreWebVitals();
    
    // Page load performance
    window.addEventListener('load', () => {
      const perfData = performance.getEntriesByType('navigation')[0];
      this.reportPerformanceMetrics(perfData);
    });
  }

  trackCoreWebVitals() {
    // LCP - Largest Contentful Paint
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      this.reportMetric('LCP', lastEntry.startTime);
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // FID - First Input Delay
    new PerformanceObserver((entryList) => {
      const firstInput = entryList.getEntries()[0];
      this.reportMetric('FID', firstInput.processingStart - firstInput.startTime);
    }).observe({ entryTypes: ['first-input'] });

    // CLS - Cumulative Layout Shift
    let clsValue = 0;
    new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      }
      this.reportMetric('CLS', clsValue);
    }).observe({ entryTypes: ['layout-shift'] });
  }

  // Search Tracking
  initializeSearchTracking() {
    // Track internal site search
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q');
    
    if (searchQuery && window.location.pathname === '/search') {
      this.trackSearchResults(searchQuery);
    }
  }

  // Tracking Methods
  trackProductView(productId) {
    // Send product view event for analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'view_item', {
        currency: window.Shopify?.currency?.active || 'USD',
        value: 0,
        items: [{
          item_id: productId,
          item_category: 'Product'
        }]
      });
    }
  }

  trackProductInteraction(productId, action) {
    // Track product interactions for SEO insights
    console.log(`Product ${action}: ${productId}`);
  }

  trackSearchQuery(query) {
    // Track search queries for SEO optimization
    if (typeof gtag !== 'undefined') {
      gtag('event', 'search', {
        search_term: query
      });
    }
  }

  trackSearchResults(query) {
    // Track search result performance
    const resultCount = document.querySelectorAll('[data-product-id]').length;
    console.log(`Search "${query}" returned ${resultCount} results`);
  }

  reportMetric(name, value) {
    // Report performance metrics
    console.log(`${name}: ${value}ms`);
    
    // Send to analytics if available
    if (typeof gtag !== 'undefined') {
      gtag('event', 'page_timing', {
        name: name,
        value: Math.round(value)
      });
    }
  }

  reportPerformanceMetrics(perfData) {
    const metrics = {
      'DOM Content Loaded': perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
      'Page Load': perfData.loadEventEnd - perfData.loadEventStart,
      'DNS Lookup': perfData.domainLookupEnd - perfData.domainLookupStart,
      'TCP Connection': perfData.connectEnd - perfData.connectStart
    };

    Object.entries(metrics).forEach(([name, value]) => {
      this.reportMetric(name, value);
    });
  }
}

// Dynamic Schema Injection
class DynamicSchemaManager {
  constructor() {
    this.injectReviewSchema();
    this.injectBreadcrumbSchema();
    this.setupDynamicPricing();
  }

  injectReviewSchema() {
    const productCards = document.querySelectorAll('[data-product-id]');
    productCards.forEach(card => {
      const rating = card.querySelector('[data-rating]')?.dataset.rating;
      const reviewCount = card.querySelector('[data-review-count]')?.dataset.reviewCount;
      
      if (rating && reviewCount) {
        const schema = {
          "@context": "https://schema.org",
          "@type": "AggregateRating",
          "ratingValue": rating,
          "reviewCount": reviewCount,
          "bestRating": "5",
          "worstRating": "1"
        };
        
        this.injectSchema(schema);
      }
    });
  }

  injectBreadcrumbSchema() {
    const breadcrumbs = document.querySelectorAll('.breadcrumb a');
    if (breadcrumbs.length > 0) {
      const schema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": Array.from(breadcrumbs).map((link, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": link.textContent.trim(),
          "item": link.href
        }))
      };
      
      this.injectSchema(schema);
    }
  }

  setupDynamicPricing() {
    // Update pricing schema when variant changes
    document.addEventListener('change', (e) => {
      if (e.target.name === 'id' && e.target.closest('[data-product-id]')) {
        this.updatePricingSchema(e.target);
      }
    });
  }

  updatePricingSchema(variantSelect) {
    const selectedOption = variantSelect.options[variantSelect.selectedIndex];
    const price = selectedOption.dataset.price;
    const sku = selectedOption.dataset.sku;
    
    if (price && sku) {
      const existingSchema = document.querySelector('#dynamic-pricing-schema');
      if (existingSchema) {
        existingSchema.remove();
      }
      
      const schema = {
        "@context": "https://schema.org",
        "@type": "Offer",
        "price": price,
        "priceCurrency": window.Shopify?.currency?.active || 'USD',
        "sku": sku,
        "availability": "https://schema.org/InStock"
      };
      
      this.injectSchema(schema, 'dynamic-pricing-schema');
    }
  }

  injectSchema(schema, id = null) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    if (id) script.id = id;
    document.head.appendChild(script);
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  new SEOOptimizer();
  new DynamicSchemaManager();
});

// Export for testing
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { SEOOptimizer, DynamicSchemaManager };
}